// UploadResourceDialog.js
// Extracted from AIWorkforcePage.js for better maintainability

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Upload, Trash2 } from 'lucide-react';
import { RESOURCE_CATEGORIES } from '../constants';

/**
 * Dialog for uploading a new resource (image) for AI agents
 * 
 * @param {Object} props
 * @param {boolean} props.open - Controls dialog visibility
 * @param {function} props.onOpenChange - Callback when dialog open state changes
 * @param {Object} props.formData - Current form data { name, description, category, tags, file, preview }
 * @param {function} props.onFormChange - Callback when form data changes
 * @param {function} props.onUpload - Callback when upload is clicked
 * @param {function} props.onCancel - Callback when cancel is clicked
 */
export const UploadResourceDialog = ({
  open,
  onOpenChange,
  formData,
  onFormChange,
  onUpload,
  onCancel
}) => {
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        onFormChange({ ...formData, file, preview: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleClearFile = () => {
    onFormChange({ ...formData, file: null, preview: null });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-indigo-500" />
            Upload Resource
          </DialogTitle>
          <DialogDescription>
            Add product photos, logos, or marketing assets for AI agents to use.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* File Upload */}
          <div>
            <Label>Image File</Label>
            <div className="mt-1">
              {formData.preview ? (
                <div className="relative">
                  <img 
                    src={formData.preview} 
                    alt="Preview" 
                    className="w-full h-48 object-cover rounded-lg border"
                  />
                  <Button
                    variant="destructive"
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={handleClearFile}
                    data-testid="resource-clear-file"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
                  <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                  <span className="text-sm text-muted-foreground">Click to upload image</span>
                  <span className="text-xs text-muted-foreground mt-1">PNG, JPG, WEBP up to 5MB</span>
                  <input
                    type="file"
                    className="hidden"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={handleFileChange}
                    data-testid="resource-file-input"
                  />
                </label>
              )}
            </div>
          </div>
          
          {/* Name */}
          <div>
            <Label htmlFor="resource-name">Name *</Label>
            <Input
              id="resource-name"
              placeholder="e.g., Product Hero Shot"
              value={formData.name || ''}
              onChange={(e) => onFormChange({ ...formData, name: e.target.value })}
              className="mt-1"
              data-testid="resource-name-input"
            />
          </div>
          
          {/* Description */}
          <div>
            <Label htmlFor="resource-desc">Description</Label>
            <Textarea
              id="resource-desc"
              placeholder="Describe the resource for better AI matching..."
              value={formData.description || ''}
              onChange={(e) => onFormChange({ ...formData, description: e.target.value })}
              className="mt-1"
              rows={2}
              data-testid="resource-description"
            />
          </div>
          
          {/* Category */}
          <div>
            <Label>Category</Label>
            <Select
              value={formData.category || 'product'}
              onValueChange={(value) => onFormChange({ ...formData, category: value })}
            >
              <SelectTrigger className="mt-1" data-testid="resource-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RESOURCE_CATEGORIES.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Tags */}
          <div>
            <Label htmlFor="resource-tags">Tags (comma separated)</Label>
            <Input
              id="resource-tags"
              placeholder="e.g., summer, outdoor, lifestyle"
              value={formData.tags || ''}
              onChange={(e) => onFormChange({ ...formData, tags: e.target.value })}
              className="mt-1"
              data-testid="resource-tags"
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="resource-cancel">
            Cancel
          </Button>
          <Button onClick={onUpload} className="bg-indigo-600 hover:bg-indigo-700" data-testid="resource-upload">
            <Upload className="h-4 w-4 mr-2" />
            Upload Resource
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UploadResourceDialog;
